import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { clsx } from 'clsx';

import type { CSSProperties } from 'react';

import styles from './Slider.module.css';

import { Icon } from 'components/Icon';

export interface SliderProps {
  /* The minimum value of the slider */
  min: number;
  /* The maximum value of the slider */
  max: number;
  /* The current value of the slider */
  value?: number;
  /* The step value of the slider */
  step?: number;
  /* The units to display next to the value */
  units?: string;
  /* aria-label labels for navigation buttons  */
  navigationLabels?: { increase: string; decrease: string };
  /* Whether the slider is disabled */
  disabled?: boolean;
  /* Callback function when the value changes */
  onChange?: (value: number) => void;
}

const HALF_STEP = 0.5;
const HUNDRED = 100;
const THUMB_SIZE = 16;
const REM = 16;

export const Slider = ({
  min,
  max,
  value,
  step = 1,
  units,
  navigationLabels,
  disabled = false,
  onChange,
}: SliderProps) => {
  const [sliderValue, setSliderValue] = useState(value !== undefined ? value : (min + max) * HALF_STEP);
  const [balloonLeftPosition, setBalloonLeftPosition] = useState('');
  const sliderRef = useRef<HTMLInputElement>(null);
  const trackWrapperRef = useRef<HTMLDivElement>(null);
  const balloonRef = useRef<HTMLDivElement>(null);

  const handleButtonClick = (direction: number) => {
    const newValue = sliderValue + direction * step;
    if (newValue >= min && newValue <= max) {
      setSliderValue(newValue);
      paintValueOnTrack(newValue);
      setFloaterPosition(newValue);
      if (onChange) {
        onChange(newValue);
      }
    }
  };

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(event.target.value);
    setSliderValue(newValue);
    paintValueOnTrack(newValue);
    setFloaterPosition(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const paintValueOnTrack = (valueToPaint: number) => {
    if (!sliderRef.current) return;
    const percentage = ((valueToPaint - min) / (max - min)) * HUNDRED;
    if (disabled) {
      sliderRef.current.style.background = `linear-gradient(
         to right, 
         var(--color-grey-700) 0%, 
         var(--color-grey-700) ${percentage}%, 
         var(--color-grey-400) ${percentage}%, 
         var(--color-grey-400) 100%)`;
    } else {
      sliderRef.current.style.background = `linear-gradient(
         to right, 
         var(--color-navy-600) 0%, 
         var(--color-navy-600) ${percentage}%, 
         var(--color-grey-400) ${percentage}%, 
         var(--color-grey-400) 100%)`;
    }
  };

  const setFloaterPosition = (value: number) => {
    if (trackWrapperRef.current && balloonRef.current) {
      const balloonWidth = balloonRef.current.clientWidth;
      const trackWidth = trackWrapperRef.current.clientWidth - THUMB_SIZE;
      const position = ((value - min) / (max - min)) * trackWidth;
      const leftPosition = position - (balloonWidth - THUMB_SIZE) * HALF_STEP;
      const clampedLeft = Math.max(0, Math.min(leftPosition, trackWidth - balloonWidth + THUMB_SIZE * HALF_STEP));
      setBalloonLeftPosition(`${clampedLeft}px`);
    }
  };

  const initializeSlider = useEffectEvent((value: number) => {
    setSliderValue(value);
    paintValueOnTrack(value);
    setFloaterPosition(value);
  });

  useEffect(() => {
    if (value !== undefined) {
      initializeSlider(value);
    }
  }, [value, disabled]);

  return (
    <div className={styles['slider_wrapper']}>
      <button
        type="button"
        className={styles['slider_button']}
        onClick={() => handleButtonClick(-1)}
        aria-label={navigationLabels?.decrease ? navigationLabels.decrease : 'Verlaag waarde'}
        disabled={disabled}
      >
        <Icon name="Minus" size={20} />
      </button>
      <div className={styles['slider_trackwrapper']} ref={trackWrapperRef}>
        <div
          className={clsx(styles['slider_balloon'], { [styles['slider_balloon--disabled']]: disabled })}
          style={{ left: balloonLeftPosition }}
          ref={balloonRef}
        >
          {sliderValue}
          {units && <span className={styles['slider_units']}>{units}</span>}
        </div>
        <input
          type="range"
          className={styles['slider_input']}
          min={min}
          max={max}
          step={step}
          value={sliderValue}
          onChange={handleSliderChange}
          ref={sliderRef}
          style={{ '--thumb-size': `${THUMB_SIZE / REM}rem` } as CSSProperties}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={sliderValue}
          aria-valuetext={units ? `${sliderValue} ${units}` : `${sliderValue}`}
          aria-label="Slider"
          disabled={disabled}
        />
      </div>
      <button
        type="button"
        className={styles['slider_button']}
        onClick={() => handleButtonClick(1)}
        aria-label={navigationLabels?.increase ? navigationLabels.increase : 'Verhoog waarde'}
        disabled={disabled}
      >
        <Icon name="Plus" size={20} />
      </button>
    </div>
  );
};
